import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Clock,
  BarChart2,
  Lock,
  Wrench,
  Compass,
  FlaskConical,
  Cpu,
  Hammer,
  ChevronRight,
} from 'lucide-react';
import { useThemeStore } from '../../stores/themeStore';
import logoColor from '../../assets/logo-color.png';
import logoMono from '../../assets/logo-mono.png';

// ─── Types ────────────────────────────────────────────────────────────────────

type FeatureStatus = 'live' | 'building' | 'queued' | 'future';

interface Feature {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  icon: React.ReactNode;
  version?: string;
}

// ─── Status config ────────────────────────────────────────────────────────────

const statusConfig: Record<FeatureStatus, { label: string; dot: string }> = {
  live:     { label: 'LIVE',     dot: 'var(--color-success)' },
  building: { label: 'IN BUILD', dot: 'var(--color-accent)'  },
  queued:   { label: 'QUEUED',   dot: 'var(--color-warn)'    },
  future:   { label: 'FUTURE',   dot: 'var(--color-text-faint)' },
};

// ─── Feature Bin card ─────────────────────────────────────────────────────────

function FeatureBin({ title, description, status, icon, version }: Feature) {
  const { label, dot } = statusConfig[status];
  return (
    <div
      style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        padding: '1.25rem',
        position: 'relative',
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-hi)')
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border)')
      }
    >
      {/* Bin label tab */}
      <div
        style={{
          position: 'absolute',
          top: '-1px',
          left: '1rem',
          backgroundColor: 'var(--color-bg)',
          padding: '0 0.4rem',
          fontSize: '0.55rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--color-text-faint)',
          fontFamily: 'var(--font-label)',
          borderLeft: '1px solid var(--color-border)',
          borderRight: '1px solid var(--color-border)',
        }}
      >
        {version ?? 'module'}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '0.25rem' }}>
        <div
          style={{
            flexShrink: 0,
            width: '2.25rem',
            height: '2.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid var(--color-border)',
            color: 'var(--color-accent)',
            backgroundColor: 'var(--color-tag-bg)',
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <h3
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.85rem',
                letterSpacing: '0.08em',
                color: 'var(--color-text)',
                margin: 0,
                textTransform: 'uppercase',
              }}
            >
              {title}
            </h3>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                fontSize: '0.55rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-label)',
                color: dot,
                border: `1px solid ${dot}`,
                padding: '0.1rem 0.4rem',
                borderRadius: '1px',
                opacity: 0.85,
              }}
            >
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: dot,
                  display: 'inline-block',
                }}
              />
              {label}
            </span>
          </div>
          <p
            style={{
              marginTop: '0.4rem',
              fontSize: '0.82rem',
              color: 'var(--color-text-muted)',
              lineHeight: 1.5,
              fontFamily: 'var(--font-body)',
            }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Spec plate footer ────────────────────────────────────────────────────────

function SpecPlate() {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-surface)',
        fontFamily: 'var(--font-heading)',
        fontSize: '0.62rem',
        letterSpacing: '0.1em',
        color: 'var(--color-text-faint)',
        padding: '0.5rem 1rem',
        display: 'flex',
        gap: '2rem',
        flexWrap: 'wrap',
        borderTop: '2px solid var(--color-border-hi)',
      }}
    >
      <span>PROJECT: <span style={{ color: 'var(--color-text-muted)' }}>OwensRoot</span></span>
      <span>REV: <span style={{ color: 'var(--color-text-muted)' }}>0.1-beta</span></span>
      <span>STATUS: <span style={{ color: 'var(--color-accent)' }}>BUILD IN PROGRESS</span></span>
      <span>DATE: <span style={{ color: 'var(--color-text-muted)' }}>{dateStr}</span></span>
      <span style={{ marginLeft: 'auto' }}>OWENSROOT © {now.getFullYear()}</span>
    </div>
  );
}

// ─── Logo — theme-aware ───────────────────────────────────────────────────────

function Logo({ height = 36 }: { height?: number }) {
  const { theme, mode } = useThemeStore();
  // Use color/vintage logo for typewriter theme or any light mode
  // Use mono/B&W logo for blueprint dark
  const useMono = theme === 'blueprint' && mode === 'dark';
  return (
    <img
      src={useMono ? logoMono : logoColor}
      alt="OwensRoot"
      style={{ height: `${height}px`, width: 'auto', display: 'block' }}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LandingPage() {
  const features: Feature[] = [
    {
      id: 'skill-tracker',
      title: 'Skill Tracker',
      description:
        'Map your deliberate practice across any discipline — code, craft, science, art. Log hours, mark milestones, watch the gaps close.',
      status: 'building',
      icon: <BookOpen size={18} />,
      version: 'v0.1',
    },
    {
      id: 'project-log',
      title: 'Project Log',
      description:
        "Every maker's graveyard of half-finished projects deserves better than a sticky note. Organize, annotate, and actually ship.",
      status: 'queued',
      icon: <Wrench size={18} />,
      version: 'v0.2',
    },
    {
      id: 'focus-timer',
      title: 'Focus Timer',
      description:
        'Deliberate practice demands deliberate time. Pomodoro-style sessions that keep you on the bench and off the feed.',
      status: 'queued',
      icon: <Clock size={18} />,
      version: 'v0.2',
    },
    {
      id: 'resource-vault',
      title: 'Resource Vault',
      description:
        'Save tutorials, datasheets, patterns, and rabbit holes per skill. The reference you need when you need it.',
      status: 'queued',
      icon: <FlaskConical size={18} />,
      version: 'v0.3',
    },
    {
      id: 'progress-analytics',
      title: 'Progress Analytics',
      description:
        'Strip-chart your learning velocity. Visualize time invested, skill momentum, and which projects are collecting dust.',
      status: 'future',
      icon: <BarChart2 size={18} />,
      version: 'v0.4',
    },
    {
      id: 'ai-assistant',
      title: 'AI Lab Assistant',
      description:
        'Premium. A research partner that knows your skill stack, your projects, and what you are still figuring out.',
      status: 'future',
      icon: <Lock size={18} />,
      version: 'Pro',
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* ── NAV BAR ────────────────────────────────────────────── */}
      <nav
        style={{
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          padding: '0 2rem',
          height: '3.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Logo height={28} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <a
            href="#features"
            style={{
              fontFamily: 'var(--font-label)',
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              textDecoration: 'none',
            }}
          >
            Features
          </a>
          <a
            href="#roadmap"
            style={{
              fontFamily: 'var(--font-label)',
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              textDecoration: 'none',
            }}
          >
            Roadmap
          </a>
          <Link
            to="/sign-in"
            style={{
              fontFamily: 'var(--font-label)',
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--color-accent)',
              textDecoration: 'none',
              border: '1px solid var(--color-accent)',
              padding: '0.25rem 0.75rem',
              borderRadius: '1px',
            }}
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section
        className="grid-paper"
        style={{
          padding: '5rem 2rem 4rem',
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'left',
        }}
      >
        {/* Descriptor above headline */}
        <div
          style={{
            fontFamily: 'var(--font-label)',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--color-text-faint)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <Compass size={12} style={{ color: 'var(--color-accent)' }} />
          <span>Skill &amp; Practice Tracking — for the self-taught polymath</span>
          <span
            style={{
              display: 'inline-block',
              width: '3rem',
              height: '1px',
              backgroundColor: 'var(--color-border)',
            }}
          />
        </div>

        {/* Headline */}
        <h1
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2.2rem, 6vw, 4rem)',
            lineHeight: 1.1,
            letterSpacing: '0.04em',
            color: 'var(--color-text)',
            margin: '0 0 0.5rem',
          }}
        >
          Root your learning.
          <br />
          <span style={{ color: 'var(--color-accent)' }}>Own your mastery.</span>
        </h1>

        {/* Sub-headline */}
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1.05rem',
            color: 'var(--color-text-muted)',
            maxWidth: '560px',
            lineHeight: 1.7,
            margin: '1.25rem 0 2.25rem',
          }}
        >
          OwensRoot is a practice log built for people who build things.
          Track deliberate practice, stack skills, document projects, and
          turn effort into proof — across every discipline you're chasing.
        </p>

        {/* CTA */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Link to="/sign-up" className="btn-primary">
            Start your root
            <ArrowRight size={14} />
          </Link>
          <a href="#features" className="btn-ghost">
            See what's inside
            <ChevronRight size={14} />
          </a>
        </div>

        {/* Feature pills */}
        <div
          style={{
            marginTop: '3rem',
            paddingTop: '1.25rem',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
          }}
        >
          {[
            { icon: <Hammer size={13} />,       text: 'Project tracking'     },
            { icon: <Cpu size={13} />,           text: 'Skill mapping'        },
            { icon: <FlaskConical size={13} />,  text: 'Resource vault'       },
            { icon: <BarChart2 size={13} />,     text: 'Progress analytics'   },
          ].map(({ icon, text }) => (
            <div
              key={text}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontFamily: 'var(--font-label)',
                fontSize: '0.62rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-text-faint)',
              }}
            >
              <span style={{ color: 'var(--color-accent)' }}>{icon}</span>
              {text}
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────── */}
      <section
        id="features"
        style={{
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          padding: '3.5rem 2rem',
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div
            style={{
              fontFamily: 'var(--font-label)',
              fontSize: '0.6rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--color-text-faint)',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span style={{ display: 'inline-block', width: '1.5rem', height: '1px', backgroundColor: 'var(--color-accent)' }} />
            Component manifest
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.4rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text)',
              margin: '0 0 2rem',
            }}
          >
            What's in the kit
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
              gap: '1px',
              backgroundColor: 'var(--color-border)',
            }}
          >
            {features.map((f) => (
              <FeatureBin key={f.id} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ── ROADMAP ──────────────────────────────────────────────── */}
      <section
        id="roadmap"
        className="grid-paper"
        style={{
          borderTop: '1px solid var(--color-border)',
          padding: '3.5rem 2rem',
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div
            style={{
              fontFamily: 'var(--font-label)',
              fontSize: '0.6rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--color-text-faint)',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span style={{ display: 'inline-block', width: '1.5rem', height: '1px', backgroundColor: 'var(--color-accent)' }} />
            Project schedule
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.4rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text)',
              margin: '0 0 2.5rem',
            }}
          >
            Build sequence
          </h2>

          <div
            style={{
              borderLeft: '2px solid var(--color-border-hi)',
              paddingLeft: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {[
              { phase: 'Phase 0', label: 'Scaffold + Governance',  desc: 'Project structure, auth framework, design system, Vercel hosting.', status: 'live'     as FeatureStatus },
              { phase: 'Phase 1', label: 'Core Features',          desc: 'Skill tracker, project log, focus timer, resource vault.',           status: 'building' as FeatureStatus },
              { phase: 'Phase 2', label: 'Database + Backend',     desc: 'Neon PostgreSQL, Drizzle ORM — persistent storage for all user data.', status: 'queued'  as FeatureStatus },
              { phase: 'Phase 3', label: 'Subscriptions',          desc: 'Stripe Free / Pro tiers. Unlock advanced features.',                  status: 'queued'  as FeatureStatus },
              { phase: 'Phase 4', label: 'AI Lab Assistant',       desc: 'Premium AI assistant via Vercel AI SDK + OpenRouter BYOK option.',    status: 'future'  as FeatureStatus },
              { phase: 'Phase 5', label: 'Analytics + Community',  desc: 'Progress dashboards, cross-skill insights, and the good stuff.',       status: 'future'  as FeatureStatus },
            ].map(({ phase, label, desc, status }) => {
              const { dot } = statusConfig[status];
              return (
                <div
                  key={phase}
                  style={{ position: 'relative', paddingBottom: '1.75rem', paddingLeft: '0.25rem' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: '-1.75rem',
                      top: '0.15rem',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: dot,
                      border: '2px solid var(--color-bg)',
                      boxShadow: `0 0 0 1px ${dot}`,
                    }}
                  />
                  <div style={{ fontFamily: 'var(--font-label)', fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: dot, marginBottom: '0.2rem' }}>{phase}</div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text)', marginBottom: '0.25rem' }}>{label}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)', maxWidth: '520px' }}>{desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          padding: '4rem 2rem',
        }}
      >
        <div style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
          <div className="title-block" style={{ marginBottom: '2rem' }}>
            <p
              style={{
                fontFamily: 'var(--font-label)',
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--color-text-faint)',
                marginBottom: '0.75rem',
              }}
            >
              Status: Early access open
            </p>
            <h2
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.6rem',
                letterSpacing: '0.04em',
                color: 'var(--color-text)',
                margin: '0 0 0.75rem',
              }}
            >
              For people who can't stop<br />
              <span style={{ color: 'var(--color-accent)' }}>learning things.</span>
            </h2>
            <p
              style={{
                fontSize: '0.88rem',
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.7,
              }}
            >
              OwensRoot is in active development. Get in early, help shape it,
              and start building the practice record you'll be proud to look back on.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/sign-up" className="btn-primary">
              Claim your root
              <ArrowRight size={14} />
            </Link>
            <Link to="/sign-in" className="btn-ghost">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <SpecPlate />
    </div>
  );
}
